import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const LegalPage = ({ title, description, path, children }) => (
  <main className="legal-page">
    <Helmet>
      <title>{title} | ADCI Online</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`https://www.adcionline.com${path}`} />
    </Helmet>
    <article className="legal-page-card">{children}</article>
  </main>
);

export const PrivacyPolicy = () => (
  <LegalPage
    title="Privacy Policy"
    description="Learn how Anees Defence Career Institute collects, uses, stores, and protects your information."
    path="/privacy-policy"
  >
    <h1>Privacy Policy</h1>
    <p><strong>Effective date:</strong> 23 August 2026</p>
    <p>Anees Defence Career Institute (ADCI), operating online through <a href="https://adcionline.com">adcionline.com</a>, respects your privacy. This policy explains how we may collect, use, store, and protect information when you interact with our website, Instagram account, social media automation, or related services.</p>

    <h2>1. Information We May Receive</h2>
    <ul>
      <li>Instagram username or account identifier</li>
      <li>Comments, messages, or enquiries you send to us</li>
      <li>Comment or message identifiers and timestamps</li>
      <li>Publicly available profile information provided by Meta</li>
    </ul>

    <h2>2. How We Use Information</h2>
    <ul>
      <li>Responding to comments, messages, and enquiries</li>
      <li>Providing information about courses, counselling, admissions, and educational services</li>
      <li>Improving customer support and service quality</li>
      <li>Preventing spam, abuse, fraud, or misuse</li>
    </ul>

    <h2>3. Automated Replies and AI Services</h2>
    <p>Some responses may be generated with automated tools and third-party AI services such as Google Gemini. We aim to send only the minimum information necessary to process a reply. Users should avoid posting sensitive personal information in public comments.</p>

    <h2>4. Sharing of Information</h2>
    <p>We do not sell or rent personal information. Information may be processed by service providers such as Meta, hosting providers, automation tools, and AI providers only as needed to operate our services.</p>

    <h2>5. Data Retention</h2>
    <p>We retain information only as long as reasonably necessary for support, security, recordkeeping, or legal compliance. Where practical, information that is no longer required is deleted or anonymized.</p>

    <h2>6. Data Security</h2>
    <p>We use reasonable technical and organizational safeguards to protect information from unauthorized access, misuse, loss, or disclosure.</p>

    <h2>7. Children's Privacy</h2>
    <p>ADCI provides educational guidance that may be relevant to students. We do not knowingly seek unnecessary sensitive personal data from children. Parents or guardians may contact us if they believe information relating to a minor should be reviewed or deleted.</p>

    <h2>8. Your Rights</h2>
    <p>You may request access to, correction of, or deletion of information we hold about you, subject to applicable law and reasonable verification.</p>

    <h2>9. Data Deletion</h2>
    <p>Deletion instructions are available on our <Link to="/data-deletion">User Data Deletion Instructions</Link> page.</p>

    <h2>10. Third-Party Platforms</h2>
    <p>Your use of Instagram, Facebook, Google, or other third-party platforms is also subject to those platforms' own privacy policies and terms.</p>

    <h2>11. Changes to This Policy</h2>
    <p>We may update this policy when our services or legal requirements change. The latest version will be published on this page.</p>

    <h2>12. Contact Us</h2>
    <p><strong>Anees Defence Career Institute (ADCI)</strong><br />
      Website: <a href="https://adcionline.com">https://adcionline.com</a><br />
      Email: <a href="mailto:kutty.anees@gmail.com">kutty.anees@gmail.com</a><br />
      Phone: +91 9890826344 | +91 9021305751
    </p>
  </LegalPage>
);

export const DataDeletion = () => (
  <LegalPage
    title="User Data Deletion Instructions"
    description="Learn how to request deletion of personal information associated with ADCI services."
    path="/data-deletion"
  >
    <h1>User Data Deletion Instructions</h1>
    <p><strong>Effective date:</strong> 23 August 2026</p>
    <p>Anees Defence Career Institute (ADCI) respects your right to request deletion of personal information associated with your interactions with our services, including Instagram comments, messages, and enquiries received through Meta APIs.</p>

    <h2>How to Request Deletion</h2>
    <p>Email <a href="mailto:kutty.anees@gmail.com">kutty.anees@gmail.com</a> with the subject <strong>Instagram Data Deletion Request</strong>.</p>

    <h2>What to Include</h2>
    <ul>
      <li>Your Instagram username or other account identifier</li>
      <li>A brief description of the information or interaction you want deleted</li>
      <li>Any additional details that help us identify the relevant record</li>
    </ul>

    <h2>Verification</h2>
    <p>For privacy and security, we may request reasonable information to verify that you are associated with the deletion request. Please do not send passwords, financial information, or unnecessary sensitive information.</p>

    <h2>What Happens Next</h2>
    <p>After receiving a valid request, we will review it and delete or anonymize applicable information within a reasonable period, subject to technical limitations and legal obligations.</p>

    <h2>Information We May Retain</h2>
    <p>In limited cases, some information may be retained where required for legal compliance, fraud prevention, security, dispute resolution, or legitimate recordkeeping.</p>

    <h2>Third-Party Platforms</h2>
    <p>If you also want information removed directly from Instagram, Facebook, Google, or another third-party platform, you may need to use that platform's own privacy or account controls.</p>

    <h2>Contact</h2>
    <p><strong>Anees Defence Career Institute (ADCI)</strong><br />
      Website: <a href="https://adcionline.com">https://adcionline.com</a><br />
      Email: <a href="mailto:kutty.anees@gmail.com">kutty.anees@gmail.com</a><br />
      Phone: +91 9890826344 | +91 9021305751
    </p>

    <p>See also our <Link to="/privacy-policy">Privacy Policy</Link>.</p>
  </LegalPage>
);
